/**
 * PermissionProtectedRoute Component
 * Protects routes based on permissions (RBAC system)
 * Also supports legacy role-based access for backward compatibility
 */
import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../../utils/authToken";
import LoadingSpinner from "../Loading/LoadingSpinner";

/**
 * PermissionProtectedRoute
 * @param {React.ReactNode} children - Child components to render if authorized
 * @param {string|string[]} requiredPermissions - Required permission(s) (OR logic)
 * @param {string[]} allowedRoles - Legacy: Allowed roles (for backward compatibility)
 * @param {boolean} requireAllPermissions - If true, requires ALL permissions (AND logic). Default: false (OR logic)
 */
function PermissionProtectedRoute({ 
  children, 
  requiredPermissions,
  allowedRoles, // Legacy support
  requireAllPermissions = false 
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();
  const { permissions } = usePermissions();

  useEffect(() => {
    const token = getToken();

    if (!token) {
      setIsAuthorized(false);
      setIsLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const registrationType = decodedToken.registerationType;
      
      // Get permissions from token directly (more reliable on refresh)
      const tokenPermissions = decodedToken.permissions || [];
      // Use context permissions if available, otherwise use token permissions
      const effectivePermissions = permissions.length > 0 ? permissions : tokenPermissions;

      // Legacy role-based check (backward compatibility)
      if (allowedRoles && !requiredPermissions) {
        const userRole = decodedToken.role;
        if (!allowedRoles.includes(userRole)) {
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
      }

      // Permission-based check
      if (requiredPermissions) {
        let hasAccess = false;

        if (requireAllPermissions) {
          // AND logic: user must have ALL permissions
          const permsArray = Array.isArray(requiredPermissions) 
            ? requiredPermissions 
            : [requiredPermissions];
          hasAccess = permsArray.every(perm => {
            // Owner has all permissions
            if (effectivePermissions.includes('*')) return true;
            return effectivePermissions.includes(perm);
          });
        } else {
          // OR logic: user must have at least ONE permission
          // Owner has all permissions
          if (effectivePermissions.includes('*')) {
            hasAccess = true;
          } else {
            const permsArray = Array.isArray(requiredPermissions) 
              ? requiredPermissions 
              : [requiredPermissions];
            hasAccess = permsArray.some(perm => effectivePermissions.includes(perm));
          }
        }

        if (!hasAccess) {
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
      }

      // Check if the route is related to mating or breeding
      const isMatingOrBreedingRoute = 
        location.pathname.includes('mating') || 
        location.pathname.includes('breeding') ||
        location.pathname.includes('breading');

      // If user is fattening type and trying to access mating/breeding routes, deny access
      if (registrationType === 'fattening' && isMatingOrBreedingRoute) {
        setIsAuthorized(false);
      } else {
        setIsAuthorized(true);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      setIsAuthorized(false);
    }

    setIsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredPermissions, allowedRoles, location.pathname, permissions]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthorized ? children : <Navigate to="/notAuthorized" replace />;
}

export default PermissionProtectedRoute;

